
import Vue from 'vue';
import { Validator, ValidationProvider, ValidationObserver } from 'vee-validate';
import { StripeConfigConstants, StripeCustomer } from "@/interfaces/Stripe";
import { PropType } from 'vue';
import { Company } from '@/interfaces/company';
import {http} from "@/config/httpCommon";
import { handleResponseException, handleResponseErrors } from '@/helpers/Helpers';
import { santaClone } from '@/helpers/Helpers';
import { StripeInvoiceTemplate } from '@/interfaces/stripeInvoiceTemplate';
import { LabelCodePair, LabelValuePair } from '@/interfaces/general';

export default Vue.extend({ 
  name: "editCompanyModal",
  components: { ValidationProvider, ValidationObserver },
  props: {
    priceLists: {
      type: Array,
      required: true,
    },
    stripeConfigConstants: {
      type: Object as PropType<StripeConfigConstants>,
      required: true,
    },
    companyEdit: {
      type: Object as PropType<Company>,
      required: true,
    },
    stripeCanadianInvoiceTemplates: {
      type: Array as PropType<StripeInvoiceTemplate[]>,
      required: true,
    },
    stripeDefaultInvoiceTemplates: {
      type: Array as PropType<StripeInvoiceTemplate[]>,
      required: true,
    },
    stripeCanadianCustomers: {
      type: Array as PropType<StripeCustomer[]>,
      required: true,
    },
    stripeDefaultCustomers: {
      type: Array as PropType<StripeCustomer[]>,
      required: true,
    },
    countries: {
      type: Array as PropType<Company['country'][]>,
      required: true,
    },
  },
  computed: {
    overrideMerchant(): boolean {
       return this.companyEdit.billing_data.override_company_details;
    },
    stripeTaxIdTypes(): Company['country']['stripe_tax_id_types'][] {
      if(this.overrideMerchant) {
        return this.companyEdit.billing_data.country?.stripe_tax_id_types || [];
      } else {
        return this.companyEdit.country?.stripe_tax_id_types || [];
      }
    },
    stripeCustomers(): StripeCustomer[] {
      const stripeApiIntegration = this.companyEdit.billing_data.stripe_api_integration.value;
      return stripeApiIntegration === "CANADIAN" ? this.stripeCanadianCustomers : this.stripeDefaultCustomers;
    },
    /**
     * What the preview shows: a file just picked (already read into a data URL), otherwise the
     * stored path resolved against file storage, and nothing at all once it has been removed.
     */
    companyLogoPreview(): string | null {
      if (this.companyLogoDataUrl) return this.companyLogoDataUrl;
      if (this.companyLogoCleared) return null;
      const stored = this.companyEdit.logo;
      if (!stored) return null;
      if (/^(https?:|data:)/.test(stored)) return stored;
      return `${process.env.VUE_APP_FILE_URL}/${stored}`;
    }
  },
  watch: {
    overrideMerchant(newVal: boolean) {
      this.companyEdit.billing_data.stripe_tax_code = null;
      this.companyEdit.billing_data.tax_identifier = null;
    },
    aiLogoQuotaUnlimited() {
      this.$nextTick(() => {
        (this.$refs.companyEditFormObserver as any).reset();
      });
    },
    promoVideoQuotaUnlimited() {
      this.$nextTick(() => {
        (this.$refs.companyEditFormObserver as any).reset();
      });
    },
    promoVideoBudgetUnlimited() {
      this.$nextTick(() => {
        (this.$refs.companyEditFormObserver as any).reset();
      });
    }
  },
  async created() {
    this.beforeUpdateData = {
      invoice_template_id: this.companyEdit.billing_data.invoice_template_id,
      country_id: this.companyEdit.country_id,
      region: this.companyEdit.region,
      billing_country_id: this.companyEdit.billing_data.country_id,
      billing_region: this.companyEdit.billing_data.region,
      stripe_integration: this.companyEdit.billing_data.stripe_api_integration.value,
    };
    this.setInvoiceTemplates();
    this.setSelectedIntegerationCountries();
    this.fetchBuyingGroups();
    this.fetchExternalSalesReps();
    this.merchantBillingEmail = this.companyEdit.settings.find(setting => setting.key_name === "merchant_billing_email")?.value?.email || null;
    await this.fetchCSManagers();
    await this.fetchSalesReps();
    this.setSelectedCSManager();
    this.setSelectedSalesRep();

    const aiLogoEnabledSetting = this.companyEdit.settings.find(s => s.key_name === "ai_logo_generation_enabled");
    this.$set(this.companyEdit, 'ai_logo_generation_enabled', aiLogoEnabledSetting?.value?.enabled ?? false);

    const aiLogoQuotaSetting = this.companyEdit.settings.find(s => s.key_name === "ai_logo_generation_daily_quota");
    const aiLogoQuota = aiLogoQuotaSetting?.value?.quota ?? 0;
    this.aiLogoQuotaUnlimited = aiLogoQuota === 0;
    this.$set(this.companyEdit, 'ai_logo_generation_daily_quota', this.aiLogoQuotaUnlimited ? 1 : aiLogoQuota);

    // enable_shops is a direct company column; coerce to boolean for the toggle.
    this.$set(this.companyEdit, 'enable_shops', Boolean(this.companyEdit.enable_shops));
    /*
     * Same for enable_popshop, but read through enable_shops: a company left with Pop Shop stored
     * as on while Shops is off must not show a ticked box, since the feature is off in practice and
     * the backend reports it that way too.
     */
    this.$set(
      this.companyEdit,
      'enable_popshop',
      Boolean(this.companyEdit.enable_shops) && Boolean(this.companyEdit.enable_popshop)
    );

    // enable_promo_video / promo_video_monthly_quota are direct company columns.
    this.$set(this.companyEdit, 'enable_promo_video', Boolean(this.companyEdit.enable_promo_video));
    const promoVideoQuota = this.companyEdit.promo_video_monthly_quota;
    this.promoVideoQuotaUnlimited = promoVideoQuota === null || promoVideoQuota === undefined;
    this.$set(this.companyEdit, 'promo_video_monthly_quota', this.promoVideoQuotaUnlimited ? 1 : promoVideoQuota);

    // promo_video_budget_cents is a direct company column (null = unlimited). Shown as an amount.
    const promoVideoBudgetCents = this.companyEdit.promo_video_budget_cents;
    this.promoVideoBudgetUnlimited = promoVideoBudgetCents === null || promoVideoBudgetCents === undefined;
    this.$set(this.companyEdit, 'promo_video_budget_amount', this.promoVideoBudgetUnlimited ? '' : (promoVideoBudgetCents / 100));
  },
  data() {
    return {
      showLoader: false, 
      beforeUpdateData: {
        invoice_template_id: null,
        country_id: null,
        region: null,
        billing_country_id: null,
        billing_region: null,
      },
      stripeInvoiceTemplates: [] as StripeInvoiceTemplate[],
      selectedIntegerationCountries: [] as (LabelCodePair & {id: number})[],
      selectedStripeCustomer: null as StripeCustomer | null,
      merchantBillingEmail: null as string,

      csManagerOptions: [] as {id: number, label: string}[],
      selectedCSManager: null as {id: number, label: string} | null,
      salesRepOptions: [] as {id: number, label: string}[],
      selectedSalesRep: null as {id: number, label: string} | null,
      csManagerLoading: false,
      salesRepLoading: false,

      buyingGroupOptions: [],
      externalSalesRepOptions: [],

      aiLogoQuotaUnlimited: false,
      /**
       * Logo state, three-valued like the API contract behind it: `null` in `companyLogoDataUrl`
       * with `companyLogoCleared === false` means "leave the stored one alone".
       */
      companyLogoDataUrl: null as string | null,
      companyLogoCleared: false,
      companyLogoError: null as string | null,
      promoVideoQuotaUnlimited: false,
      promoVideoBudgetUnlimited: false,
    };
  },
  methods: {
    /** Read the picked file into a data URL — the update payload carries base64, not multipart. */
    onCompanyLogoSelected(event: Event) {
      this.companyLogoError = null;
      const input = event.target as HTMLInputElement;
      const file = input?.files?.[0];
      if (!file) return;
      if (!file.type.startsWith('image/')) {
        this.companyLogoError = 'Pick an image file (PNG, JPG, SVG or WEBP).';
        return;
      }
      const MAX_LOGO_BYTES = 5 * 1024 * 1024;
      if (file.size > MAX_LOGO_BYTES) {
        this.companyLogoError = 'That file is over 5MB. Try a smaller export.';
        return;
      }
      const reader = new FileReader();
      reader.onload = () => {
        this.companyLogoDataUrl = typeof reader.result === 'string' ? reader.result : null;
        // Picking a file after a removal is a replacement, not a removal followed by an upload.
        this.companyLogoCleared = false;
      };
      reader.onerror = () => {
        this.companyLogoError = 'That file could not be read. Try another one.';
      };
      reader.readAsDataURL(file);
    },

    /** Drop the logo. Saved as an explicit `null` so the backend deletes the stored object. */
    removeCompanyLogo() {
      this.companyLogoDataUrl = null;
      this.companyLogoCleared = true;
      this.companyLogoError = null;
      const input = this.$refs.companyLogoInput as any;
      input?.reset?.();
    },

    /**
     * The `logo` value the save sends: the new file, an explicit `null` to clear, or the stored path
     * unchanged. Never omitted, so the backend always knows which of the three this is.
     */
    companyLogoPayloadValue(): string | null {
      if (this.companyLogoDataUrl) return this.companyLogoDataUrl;
      if (this.companyLogoCleared) return null;
      return this.companyEdit.logo || null;
    },

    async fetchBuyingGroups() {
      try {
        const response = await http.get('/buying-groups-sales-reps/buying-groups');
        if (response.data.success) {
          this.buyingGroupOptions = response.data.data;
        }
      } catch (error) {
        console.error('Failed to fetch buying groups:', error);
      }
    },
    
    async fetchExternalSalesReps() {
      try {
        const response = await http.get('/buying-groups-sales-reps/external-sales-reps');
        if (response.data.success) {
          this.externalSalesRepOptions = response.data.data;
        }
      } catch (error) {
        console.error('Failed to fetch external sales reps:', error);
      }
    },
    handleBuyingGroupChange(selectedGroup: any) {
      // Keep as array but with single selection
      if (selectedGroup) {
        this.companyEdit.buying_groups = [selectedGroup];
      } else {
        this.companyEdit.buying_groups = [];
      }
    },
    
    handleExternalSalesRepChange(selectedRep: any) {
      // Keep as array but with single selection
      if (selectedRep) {
        this.companyEdit.external_sales_reps = [selectedRep];
      } else {
        this.companyEdit.external_sales_reps = [];
      }
    },
    updateCompany() {
      this.$refs.companyEditFormObserver.validate().then(async valid => {
        if (valid) {
          const companyUpdatePayload = this.getCompanyUpdatePayload();
          this.showLoader = true;
          await http.put('/companies/'+this.companyEdit.id, companyUpdatePayload).then(res => {
            /*
             * The stored path the backend just wrote is now the truth; the pending file is spent.
             * Guarded on the key being present rather than on its value — a response that omits
             * `logo` must not read as "the logo was removed".
             */
            const savedLogo = res.data.result && Object.prototype.hasOwnProperty.call(res.data.result, 'logo')
              ? res.data.result.logo
              : (this.companyLogoCleared ? null : this.companyEdit.logo);
            this.$set(this.companyEdit, 'logo', savedLogo || null);
            this.companyLogoDataUrl = null;
            this.companyLogoCleared = false;
            this.$emit('company-updated', res.data.result);
            this.showLoader = false;
          }).catch((errorResponse) => {
            this.showLoader = false;
            this.handleResponseErrors(errorResponse)
          });
        }
      });
    },
    getCompanyUpdatePayload() {
      const updatedCompany = santaClone(this.companyEdit);
      let stripeId = updatedCompany.billing_data.stripe_id || null;
      if(this.selectedStripeCustomer) {
        stripeId = this.selectedStripeCustomer.id;
      }
      let companyCountry = {
        id: updatedCompany.country.id,
        label: updatedCompany.country.label,
        code: updatedCompany.country.code,
      };
      let billingCountry = null
      if(this.companyEdit.billing_data.country) {
        billingCountry = {
          id: updatedCompany.billing_data.country.id,
          label: updatedCompany.billing_data.country.label,
          code: updatedCompany.billing_data.country.code,
        };
      }
      // let companyCountry = 
      const companyUpdatedPayload = {
        company_name: updatedCompany.company_name,
        address: updatedCompany.address,
        country: companyCountry,
        region: updatedCompany.region,
        city: updatedCompany.city,
        post_code: updatedCompany.post_code,
        company_type: updatedCompany.company_type,
        phone: updatedCompany.phone,
        pricelist_id: updatedCompany.pricelist?.id || null,
        merchant_billing_email: this.merchantBillingEmail,

        cs_manager_id: this.selectedCSManager ? this.selectedCSManager.id : null,
        sales_representative_id: this.selectedSalesRep ? this.selectedSalesRep.id : null,

        buying_group_ids: updatedCompany.buying_groups?.map(bg => bg.id) || [],
        external_sales_rep_ids: updatedCompany.external_sales_reps?.map(sr => sr.id) || [],

        ai_logo_generation_enabled: Boolean(updatedCompany.ai_logo_generation_enabled),
        ai_logo_generation_unlimited: this.aiLogoQuotaUnlimited,
        ai_logo_generation_daily_quota: this.aiLogoQuotaUnlimited ? null : (() => {
          const v = updatedCompany.ai_logo_generation_daily_quota;
          const n = typeof v === 'string' ? parseInt(v, 10) : Number(v);
          return Number.isFinite(n) && n >= 1 ? Math.floor(n) : 1;
        })(),

        enable_shops: Boolean(updatedCompany.enable_shops),

        logo: this.companyLogoPayloadValue(),
        /*
         * Never sent as true without Shops. The backend rejects that pairing with a 422, so clearing
         * it here keeps "untick Shops and save" a working action rather than a validation error the
         * admin has to decode — the checkbox is disabled in that state, but its last value survives.
         */
        enable_popshop: Boolean(updatedCompany.enable_shops) && Boolean(updatedCompany.enable_popshop),
        enable_promo_video: Boolean(updatedCompany.enable_promo_video),
        promo_video_monthly_quota: this.promoVideoQuotaUnlimited ? null : (() => {
          const v = updatedCompany.promo_video_monthly_quota;
          const n = typeof v === 'string' ? parseInt(v, 10) : Number(v);
          return Number.isFinite(n) && n >= 1 ? Math.floor(n) : 1;
        })(),
        promo_video_budget_cents: this.promoVideoBudgetUnlimited ? null : (() => {
          const v = updatedCompany.promo_video_budget_amount;
          const n = typeof v === 'string' ? parseFloat(v) : Number(v);
          return Number.isFinite(n) && n >= 0 ? Math.round(n * 100) : 0;
        })(),

        billing_data: {
          override_company_details: updatedCompany.billing_data.override_company_details,
          payment_method: updatedCompany.billing_data.payment_method?.value || null,
          payment_days_until_due: updatedCompany.billing_data.payment_days_until_due,
          tax_identifier: updatedCompany.billing_data.tax_identifier || null,
          stripe_tax_code: updatedCompany.billing_data.stripe_tax_code?.tax_id_type || null,
          stripe_api_integration: updatedCompany.billing_data.stripe_api_integration?.value || null,
          invoice_template_id: updatedCompany.billing_data.invoice_template_id?.id || null,
          address1: updatedCompany.billing_data.address1,
          address2: updatedCompany.billing_data.address2,
          name: updatedCompany.billing_data.name,
          country: billingCountry,
          region: updatedCompany.billing_data.region,
          city: updatedCompany.billing_data.city,
          zip_code: updatedCompany.billing_data.zip_code,
          stripe_id: stripeId,
          tax_status: updatedCompany.billing_data.tax_status,
        }
      }
      return companyUpdatedPayload;
    },
    handleOverrideMerchantChange(updatedValue: boolean) {
      this.$refs.companyEditFormObserver.validate();
    },
    async handleStripeIntegrationChange(updatedIntegeration: LabelValuePair) {
      const stripeIntegerationUpdated = this.beforeUpdateData.stripe_integration !== updatedIntegeration.value;
      let stripeIntegerationChangeConfirmation = true;
      if(stripeIntegerationUpdated) {
        stripeIntegerationChangeConfirmation = await this.$confirm("Stripe Integration has been changed. Please confirm to proceed.", null, null, {allowOutsideClick: false}).catch(() => false);
      }
      if(stripeIntegerationChangeConfirmation) {
       this.companyEdit.billing_data.stripe_api_integration = updatedIntegeration;
       this.selectedStripeCustomer = null;
       this.setInvoiceTemplates();
       this.setSelectedIntegerationCountries();
      }
    },
    setInvoiceTemplates() {
      const stripeApiIntegration = this.companyEdit.billing_data.stripe_api_integration.value;
      this.stripeInvoiceTemplates = stripeApiIntegration === "CANADIAN" ? this.stripeCanadianInvoiceTemplates : this.stripeDefaultInvoiceTemplates;
      this.companyEdit.billing_data.invoice_template_id = this.stripeInvoiceTemplates.find(invoiceTemplate => invoiceTemplate.id === this.beforeUpdateData.invoice_template_id) || null;
    },
    setSelectedIntegerationCountries() {
      const stripeApiIntegration = this.companyEdit.billing_data.stripe_api_integration.value;
      if(stripeApiIntegration === "CANADIAN") {
        this.selectedIntegerationCountries = this.countries.filter(country => country.id === 39);
      } else {
        this.selectedIntegerationCountries = this.countries.filter(country => country.id !== 39);
      }
      this.companyEdit.country = this.selectedIntegerationCountries.find((country: LabelCodePair & {id: number}) => country.id === this.beforeUpdateData.country_id) || null;

      this.companyEdit.country_id = this.companyEdit.country ? this.companyEdit.country.id : null;
      this.companyEdit.billing_data.country = this.selectedIntegerationCountries.find((country: LabelCodePair & {id: number}) => country.id === this.beforeUpdateData.billing_country_id) || null;
      this.companyEdit.billing_data.country_id = this.companyEdit.billing_data.country ? this.companyEdit.billing_data.country.id : null;
      this.setSelectedCountryState();
      
    },
    handleCountryChange(type: 'company_country' | 'billing_country', country: Company['country']  ) {
      if(type === 'company_country') {
        this.companyEdit.country_id = country.id;
      } else {
        this.companyEdit.billing_data.country_id = country.id;
      }
      this.setSelectedCountryState();
    },
    setSelectedCountryState() {
      this.$nextTick(() => {
        if(this.companyEdit.country?.id && this.companyEdit.country.states.length > 0 && this.beforeUpdateData.region) {
          this.companyEdit.region = this.companyEdit.country.states.find(state => state.code === this.beforeUpdateData?.region?.code) || null;
        } else {
          this.companyEdit.region = null;
        }
        if(this.companyEdit.billing_data?.country?.id && this.companyEdit.billing_data.country.states.length > 0 && this.beforeUpdateData.billing_region) {
          this.companyEdit.billing_data.region = this.companyEdit.billing_data.country.states.find(state => state.code === this.beforeUpdateData?.billing_region?.code) || null;
        } else {
          this.companyEdit.billing_data.region = null;
        }
      });
    },
    async fetchCSManagers() {
      try {
        this.csManagerLoading = true;
        const res = await http.get(`role/users?role=csManager`);
        if (res.data.result && res.data.result.users) {
          interface UserResponse {
            id: number;
            name?: string;
            email?: string;
          }
          this.csManagerOptions = res.data.result.users.map((user: UserResponse) => ({
            id: user.id,
            label: user.name || user.email || ''
          }));
        }
      } catch (error) {
        console.error('Error fetching CS Managers:', error);
        handleResponseException(error);
      } finally {
        this.csManagerLoading = false;
      }
    },
    async fetchSalesReps() {
      try {
        this.salesRepLoading = true;
        const res = await http.get(`role/users?role=salesRepresentative`);
        if (res.data.result && res.data.result.users) {
          interface UserResponse {
            id: number;
            name?: string;
            email?: string;
          }
          this.salesRepOptions = res.data.result.users.map((user: UserResponse) => ({
            id: user.id,
            label: user.name || user.email || ''
          }));
        }
      } catch (error) {
        console.error('Error fetching Sales Representatives:', error);
        handleResponseException(error);
      } finally {
        this.salesRepLoading = false;
      }
    },
    setSelectedCSManager() {
      // Check if company has a CS Manager assigned
      // The company object might have cs_manager or cs_manager_id field
      // Adjust this based on your actual API response structure
      interface CompanyWithCSManager extends Company {
        cs_manager_id?: number;
        cs_manager?: { id: number };
      }
      const company = this.companyEdit as CompanyWithCSManager;
      if (company.cs_manager_id) {
        const csManagerId = company.cs_manager_id;
        this.selectedCSManager = this.csManagerOptions.find(manager => manager.id === csManagerId) || null;
      } else if (company.cs_manager) {
        const csManager = company.cs_manager;
        this.selectedCSManager = this.csManagerOptions.find(manager => manager.id === csManager.id) || null;
      }
    },
    setSelectedSalesRep() {
      interface CompanyWithSalesRep extends Company {
        sales_representative_id?: number;
        sales_representative?: { id: number };
      }
      const company = this.companyEdit as CompanyWithSalesRep;
      if (company.sales_representative_id) {
        const repId = company.sales_representative_id;
        this.selectedSalesRep = this.salesRepOptions.find(rep => rep.id === repId) || null;
      } else if (company.sales_representative) {
        const rep = company.sales_representative;
        this.selectedSalesRep = this.salesRepOptions.find(r => r.id === rep.id) || null;
      }
    }
  }
});
